import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import type { RemoteFile, SyncedLocalFile } from '@actual-app/core/types/file';
import type { CategoryEntity } from '@actual-app/core/types/models';
import type { Template } from '@actual-app/core/types/models/templates';

import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNotes } from '#hooks/useNotes';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { useSelector } from '#redux';

import { objetivoDesdePlantillas } from './objetivos';
import type { Objetivo } from './objetivos';
import { parsePagosFijos } from './pagadas';
import { cicloDe, esObjetivoConFecha, ridDe } from './recuerdos';
import type { Recuerdo } from './recuerdos';
import {
  esBarcoCompartido,
  tipoDeCategoria,
  travesiaActual,
} from './travesias';
import type { TipoCategoria } from './travesias';
import { useRecuerdos } from './useRecuerdos';

export function plantillasDe(goalDef: string | null | undefined): Template[] {
  if (!goalDef) {
    return [];
  }
  try {
    const v: unknown = JSON.parse(goalDef);
    return Array.isArray(v) ? (v as Template[]) : [];
  } catch {
    return [];
  }
}

export type TravesiaCategoria = {
  objetivo: Objetivo | null | 'otro';
  esFijo: boolean;
  tipo: TipoCategoria;
  /** Travesía navegando de la categoría (la del ciclo actual si la hay). */
  recuerdo: Recuerdo | null;
};

/** Objetivo, tipo y travesía navegando de una categoría. */
export function useTravesiaCategoria(
  category: CategoryEntity | null | undefined,
): TravesiaCategoria {
  const nota = useNotes(category?.id ?? '');
  const [rawFijos] = useSyncedPref('pagos-fijos');
  const recuerdos = useRecuerdos();
  return useMemo(() => {
    const objetivo = category?.goal_def
      ? objetivoDesdePlantillas(plantillasDe(category.goal_def), nota)
      : null;
    const esFijo = !!category && parsePagosFijos(rawFijos).has(category.id);
    const rid =
      category && esObjetivoConFecha(objetivo)
        ? ridDe(category.id, cicloDe(objetivo, monthUtils.currentDay()))
        : null;
    return {
      objetivo,
      esFijo,
      tipo: tipoDeCategoria({ objetivo, esFijo }),
      recuerdo: category ? travesiaActual(recuerdos, category.id, rid) : null,
    };
  }, [category, nota, rawFijos, recuerdos]);
}

/** El presupuesto abierto lo comparte otra persona (barco compartido). */
export function useBarcoCompartido(): boolean {
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const allFiles = useSelector(state => state.budgetfiles.allFiles || []);
  const userData = useSelector(state => state.user.data);
  const archivo = allFiles.find(
    (file): file is SyncedLocalFile | RemoteFile =>
      (file.state === 'remote' ||
        file.state === 'synced' ||
        file.state === 'detached') &&
      file.cloudFileId === cloudFileId,
  );
  return esBarcoCompartido(archivo, userData?.userId);
}
